import React from 'react';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import styled from 'styled-components';
import { textOverflow } from 'app/shared/styles/_mixins';

import './style.scss';
import Linker from 'app/shared/modules/Linker';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import Text from 'app/shared/core/Text';

/**
 * Array of breadcrumbs segment objects, like this:
 *   [{
 *       name: 'Home'
 *       uri: 'https://hotpads/'
 *   }, {
 *       name: 'Unavailable'
 *       unavailable: true
 *   }, {
 *       name: 'Current'
 *       uri: 'https://hotpads/unavailable'
 *   }]
 *
 * Each segment will be rendered in order, like this
 *
 *   <ul>
 *       <li><a href="https://hotpads/">Home</a></li>
 *       <li><a>Unavailable</a></li>
 *       <li><a href="https://hotpads/unavailable">Current</a></li>
 *   </ul>
 *
 * @param {string} name - Link name renders as text content.
 * @param {string} uri - Link target.
 * @param {boolean} unavailable - Render link as unavailable.
 */
const StyledList = styled.ul`
  ${textOverflow};
`;

type BreadcrumbSegment = {
  name: string;
  uri?: string;
  unavailable?: boolean;
};

type OwnProps = {
  breadcrumbs?: Array<BreadcrumbSegment>;
};

class HpBreadcrumb extends React.Component<PropsFromRedux & OwnProps> {
  static defaultProps = {
    breadcrumbs: [],
  };

  handleClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string): void => {
    const { dispatch } = this.props;
    if (e) {
      e.preventDefault();
    }

    // @ts-expect-error - Redux thunk actions not typed correctly
    dispatch(RouteActions.transitionToUrlWithFilterQuery(url));
  };

  renderBreadcrumb = (segment: BreadcrumbSegment | undefined): React.ReactNode => {
    if (segment && segment.uri) {
      return (
        <Linker
          linkType="accent"
          to={segment.uri}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => this.handleClick(e, segment.uri!)}
        >
          <span>{segment.name}</span>
        </Linker>
      );
    } else if (segment) {
      return <span>{segment.name}</span>;
    } else {
      return null;
    }
  };

  render(): React.ReactNode {
    const { breadcrumbs = [] } = this.props;

    return (
      <StyledList className="HpBreadcrumb">
        {breadcrumbs.map((segment, index, list) => {
          const position = index + 1;
          const lastSegment = position === list.length;

          return (
            <li className="HpBreadcrumb-item" key={`breadcrumb-${position}`}>
              <Text size="sm">
                {this.renderBreadcrumb(segment)}
                {!lastSegment && ' / '}
              </Text>
            </li>
          );
        })}
      </StyledList>
    );
  }
}

const connector = connect();
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(HpBreadcrumb);
